import { computed, ref } from "vue";
import { useNgSession } from "@sazka/ng/ng-session";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { BonusomatPlayerStatus } from "@/loyalty/api/models/bonusomat-player-status";
import { fetchBonusomatPlayerStatus } from "@/loyalty/api/loyalty-api";

const { session } = useNgSession();
const playerId = computed(() => session.value?.PlayerID);

const maxBonusomatPoints = 5;

// PLAYER DATA variables
const bonusomatPlayerData = ref<BonusomatPlayerStatus>({
    access: { denied: true },
    points: 0,
    lastSpin: null,
    activeQuestId: null,
});

const hasMaxPoints = computed(() => bonusomatPlayerData.value?.points >= maxBonusomatPoints);

const remainingSpinsCount = computed(() => Math.trunc(bonusomatPlayerData.value.points / maxBonusomatPoints));

const isPlayerDataLoaded = ref(false);
const playerDataHasError = ref(false);

const fulfilledTextInclined = computed(() => {
    switch (bonusomatPlayerData.value.points) {
        case 1:
            return "Splněn";
        case 2:
        case 3:
        case 4:
            return "Splněné";
        default:
            return "Splněných";
    }
});

// PLAYER DATA METODS
async function loadBonusomatPlayerData() {
    if (!playerId.value) {
        clearPlayerData();
        return;
    }

    try {
        bonusomatPlayerData.value = await fetchBonusomatPlayerStatus(playerId.value);
    } catch (error) {
        playerDataHasError.value = true;
        sazkaAlert(
            "Omlouváme se, při načítání dat hráče z Bonusomatu nastala chyba.",
            "Některé jeho části nemusí správně fungovat.<br/>Zkuste to prosím později."
        );
        logException("bonusomat-player", error, { message: "Load bonusomat player error" });
    } finally {
        isPlayerDataLoaded.value = true;
    }
}

function clearPlayerData() {
    bonusomatPlayerData.value = {
        access: { denied: true },
        points: 0,
        lastSpin: null,
        activeQuestId: null,
    };

    isPlayerDataLoaded.value = false;
}

async function updateBonusomatPoints(newPoints: number) {
    bonusomatPlayerData.value.points = newPoints;
}

async function updateBonusomatData(newPoints: number, denied: boolean) {
    bonusomatPlayerData.value.points = newPoints;
    bonusomatPlayerData.value.access.denied = denied;
}

export default {
    bonusomatPlayerData,
    hasMaxPoints,
    remainingSpinsCount,
    isPlayerDataLoaded,
    playerDataHasError,
    maxBonusomatPoints,
    fulfilledTextInclined,

    loadBonusomatPlayerData,
    clearPlayerData,
    updateBonusomatPoints,
    updateBonusomatData,
};
